import React, { useState, useEffect, useRef } from "react";
import { X, Plus, Trash2, Upload, Image as ImageIcon, Scan, Hash, Tag, Sparkles } from "lucide-react";
import SearchableSelect from "./SearchableSelect";
import BarcodeScanInput from "./BarcodeScanInput";
import { masterService } from "../api/masterService";
import { useNotification } from "../contexts/NotificationContext";
import {
  formatNumberWithSeparator,
  parseNumberFromSeparator,
} from "../utils/numberFormat";
import { compressImageToBase64 } from "../utils/imageCompression";
import { getImageUrl } from "../utils/imageUtils";

export default function AddProductModal({
  isOpen,
  onClose,
  initialData,
  hasVariants,
  onSuccess,
}) {
  const { showSuccess, showError } = useNotification();
  const isApps = localStorage.getItem("PREVIEW_APPS") === "true";
  const [margin, setMargin] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  // Form States
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [barcode, setBarcode] = useState("");
  const [sku, setSku] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [buyPrice, setBuyPrice] = useState("");
  const [unitId, setUnitId] = useState("");
  const [sellPrice, setSellPrice] = useState("");
  const [stok, setStok] = useState("");
  const [status, setStatus] = useState("Aktif");
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState("");
  const fileInputRef = useRef(null);

  // Variant rows (opsional). Hanya untuk mode create/edit produk.
  const [variants, setVariants] = useState([]);

  // Dropdown options
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);

  const handleAddCategory = async (name) => {
    try {
      const res = await masterService.categories.create({ name });
      const newCat = res.data?.data || res.data;
      if (newCat && newCat.id) {
        setCategories((prev) => [newCat, ...prev]);
        setCategoryId(newCat.id);
        showSuccess(`Kategori "${newCat.name}" berhasil ditambahkan`);
        return newCat;
      }
    } catch (err) {
      showError(err.message || "Gagal menambah kategori");
      throw err;
    }
  };

  const handleAddUnit = async (name) => {
    try {
      const unitCode = name.toUpperCase().replace(/\s+/g, "_");
      const res = await masterService.units.create({ name, code: unitCode });
      const newUnit = res.data?.data || res.data;
      if (newUnit && newUnit.id) {
        setUnits((prev) => [newUnit, ...prev]);
        setUnitId(newUnit.id);
        showSuccess(`Satuan "${newUnit.name}" berhasil ditambahkan`);
        return newUnit;
      }
    } catch (err) {
      showError(err.message || "Gagal menambah satuan");
      throw err;
    }
  };

  // Load categories and units
  useEffect(() => {
    if (isOpen) {
      setLoadingData(true);
      Promise.all([
        masterService.categories.listAll({ status: "ACTIVE" }),
        masterService.units.listAll({ status: "ACTIVE" }),
      ])
        .then(([categoriesRes, unitsRes]) => {
          setCategories(Array.isArray(categoriesRes) ? categoriesRes : categoriesRes.data || []);
          setUnits(Array.isArray(unitsRes) ? unitsRes : unitsRes.data || []);
        })
        .catch((err) => {
          showError(err.message || "Gagal memuat data kategori dan satuan");
        })
        .finally(() => setLoadingData(false));
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && initialData) {
      const buy = initialData.purchase_price || initialData.buyPrice || 0;
      const sell = initialData.sell_price || initialData.sellPrice || 0;

      setName(initialData.name || "");
      setCode(initialData.code || "");
      setBarcode(initialData.barcode || "");
      setSku(initialData.sku || "");
      setCategoryId(initialData.category_id || "");
      setBuyPrice(buy ? buy.toString() : "");
      setUnitId(initialData.unit_id || "");
      setSellPrice(sell ? sell.toString() : "");
      setStok(initialData.stok !== undefined && initialData.stok !== null ? initialData.stok.toString() : "");
      setImagePreview(getImageUrl(initialData.image_url) || null);
      setImageBase64("");

      // Calculate margin from buy and sell price
      if (buy > 0 && sell > 0) {
        const calculatedMargin = ((sell - buy) / buy) * 100;
        setMargin(Math.round(calculatedMargin * 100) / 100);
      } else {
        setMargin(initialData.margin || 0);
      }

      setStatus(
        initialData.status === "ACTIVE"
          ? "Aktif"
          : initialData.status || "Aktif",
      );
      if (initialData.variants && Array.isArray(initialData.variants)) {
        setVariants(
          initialData.variants.map((v) => ({
            id: v.id,
            name: v.name || "",
            purchase_price: (v.purchase_price || initialData.purchase_price || "").toString(),
            price: (v.sell_price || v.price || initialData.sell_price || "").toString(),
            stok: (v.stok !== undefined && v.stok !== null ? v.stok : "").toString(),
          }))
        );
      } else {
        setVariants([]);
      }
    } else if (isOpen) {
      // Reset form if opening in add mode
      setName("");
      setCode("");
      setBarcode("");
      setSku("");
      setCategoryId("");
      setBuyPrice("");
      setUnitId("");
      setSellPrice("");
      setStok("");
      setMargin(0);
      setStatus("Aktif");
      setVariants([]);
      setImagePreview(null);
      setImageBase64("");
    }
  }, [isOpen, initialData]);

  // Calculate margin when sell price changes
  const handleSellPriceChange = (value) => {
    const rawValue = value.replace(/\./g, "");
    setSellPrice(rawValue);

    const buy = parseNumberFromSeparator(buyPrice);
    const sell = parseNumberFromSeparator(rawValue);

    if (buy > 0 && sell > 0) {
      const calculatedMargin = ((sell - buy) / buy) * 100;
      setMargin(Math.round(calculatedMargin * 100) / 100);
    } else {
      setMargin(0);
    }
  };

  // Calculate sell price when margin changes
  const handleMarginChange = (value) => {
    setMargin(value);

    const buy = parseNumberFromSeparator(buyPrice);
    const marginValue = parseFloat(value) || 0;

    if (buy > 0 && marginValue >= 0) {
      const calculatedSellPrice = buy + (buy * marginValue) / 100;
      setSellPrice(Math.round(calculatedSellPrice).toString());
    }
  };

  // Recalculate margin when buy price changes
  const handleBuyPriceChange = (value) => {
    const rawValue = value.replace(/\./g, "");
    setBuyPrice(rawValue);

    const buy = parseNumberFromSeparator(rawValue);
    const sell = parseNumberFromSeparator(sellPrice);

    if (buy > 0 && sell > 0) {
      const calculatedMargin = ((sell - buy) / buy) * 100;
      setMargin(Math.round(calculatedMargin * 100) / 100);
    } else {
      setMargin(0);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      showError("Nama produk harus diisi");
      return;
    }
    if (!categoryId) {
      showError("Kategori harus dipilih");
      return;
    }
    if (!unitId) {
      showError("Satuan harus dipilih");
      return;
    }
    if (!hasVariants && variants.length === 0 && !sellPrice) {
      showError("Harga jual harus diisi");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: name.trim(),
        code: code.trim() || undefined,
        barcode: barcode.trim() || undefined,
        sku: sku.trim() || undefined,
        category_id: categoryId,
        unit_id: unitId,
        purchase_price: buyPrice
          ? parseNumberFromSeparator(buyPrice)
          : 0,
        sell_price: sellPrice ? parseNumberFromSeparator(sellPrice) : 0,
        stok: stok ? parseNumberFromSeparator(stok) : 0,
        image_base64: imageBase64 || (initialData?.image_url && !imagePreview ? "" : undefined),
        status: status === "Aktif" ? "ACTIVE" : "INACTIVE",
      };

      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });
      
      const validVariants = variants
        .filter((v) => v.name?.trim())
        .map((v) => ({
          name: v.name.trim(),
          unit_id: unitId || undefined,
          purchase_price:
            parseNumberFromSeparator(v.purchase_price) ||
            parseNumberFromSeparator(buyPrice) ||
            0,
          sell_price:
            parseNumberFromSeparator(v.price) ||
            parseNumberFromSeparator(sellPrice) ||
            0,
          stok: parseNumberFromSeparator(v.stok) || 0,
        }));

      if (validVariants.length > 0) {
        payload.variants = validVariants;
      }

      if (initialData?.id && validVariants.length === 0) {
        payload.variants = [];
      }

      if (initialData?.id) {
        await masterService.products.update(initialData.id, payload);
        showSuccess("Produk berhasil diperbarui");
      } else {
        await masterService.products.create(payload);
        showSuccess("Produk berhasil ditambahkan");
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Error saving product:", err);
      showError(err.message || "Gagal menyimpan produk");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-2xl w-full max-w-2xl lg:max-w-3xl flex flex-col max-h-[92vh] overflow-hidden modal-panel-enter">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-teal-600 text-white rounded-t-2xl">
          <h2 className="text-base font-semibold tracking-tight">
            {initialData ? "Edit Data Produk" : "Tambah Data Produk"}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5 overflow-y-auto">
          {/* Upload Gambar Produk (Tampil di Tambah & Edit) */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Gambar Produk <span className="text-slate-400 font-normal">(opsional)</span>
            </label>
            <div className="flex items-center gap-4 bg-slate-50/80 p-3.5 rounded-xl border border-slate-200/80">
              <div
                onClick={() => fileInputRef.current?.click()}
                className="w-20 h-20 rounded-xl border-2 border-dashed border-slate-300 bg-white flex flex-col items-center justify-center cursor-pointer hover:bg-teal-50/50 hover:border-teal-400 transition-all relative overflow-hidden shrink-0 group shadow-xs">
                {imagePreview ? (
                  <div className="w-full h-full relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setImagePreview(null);
                        setImageBase64("");
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      className="absolute top-1 right-1 bg-white/90 p-1 rounded-md hover:bg-rose-100 text-slate-500 hover:text-rose-600 transition-all shadow-sm z-10"
                      title="Hapus Gambar"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-slate-400 group-hover:text-teal-600 transition-colors">
                    <ImageIcon className="w-5 h-5 mb-1" />
                    <span className="text-[10px] font-semibold">Upload</span>
                  </div>
                )}
              </div>
              <div className="flex-1 text-xs text-slate-500 space-y-1">
                <p className="font-semibold text-slate-800">Unggah Foto Produk</p>
                <p className="text-[11px] text-slate-400">Format yang didukung: JPG, JPEG, PNG (Maksimal 5MB).</p>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-1 px-3 py-1 bg-white border border-slate-200 hover:border-teal-500 text-teal-600 hover:bg-teal-50 text-xs font-semibold rounded-lg transition-colors inline-flex items-center gap-1.5 shadow-xs">
                  <Upload className="w-3.5 h-3.5" />
                  {imagePreview ? "Ganti Gambar" : "Pilih File Gambar"}
                </button>
              </div>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/png, image/jpeg, image/jpg"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;

                if (file.size > 5 * 1024 * 1024) {
                  showError("Ukuran gambar tidak boleh lebih dari 5MB");
                  return;
                }

                try {
                  const base64 = await compressImageToBase64(file);
                  setImagePreview(base64);
                  setImageBase64(base64);
                } catch (error) {
                  console.error("Gagal memproses gambar:", error);
                  showError("Gagal memproses gambar");
                }
              }}
            />
          </div>

          {/* Informasi Utama: Nama Produk & Kategori */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Nama Produk <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
                placeholder="Contoh: Air Filter Radial 131-8822 X"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Kategori <span className="text-rose-500">*</span>
              </label>
              <SearchableSelect
                options={categories}
                value={categoryId}
                onChange={(value) => setCategoryId(value)}
                onAddNew={handleAddCategory}
                addNewLabel="Tambah Kategori Baru"
                maxOptionsHeight="max-h-48"
                fetchOptions={async (term) => {
                  const response = await masterService.categories.list({
                    status: "ACTIVE",
                    query: term,
                  });
                  return response.data || [];
                }}
                minSearchLength={1}
                placeholder="Pilih kategori..."
                disabled={loading || loadingData}
                displayKey="name"
                valueKey="id"
              />
            </div>
          </div>

          {/* Barcode & SKU - Khusus Retail */}
          {!isApps && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Barcode / EAN
                </label>
                <BarcodeScanInput
                  value={barcode}
                  onChange={(val) => setBarcode(val)}
                  placeholder="Contoh: 899123456789"
                  disabled={loading}
                  className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 transition-colors"
                  focusClass="focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Kode SKU
                </label>
                <input
                  type="text"
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
                  placeholder="Contoh: SKU-PER-001"
                  disabled={loading}
                />
              </div>
            </div>
          )}

          {/* Keuangan & Satuan */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Harga Beli (Modal)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm font-medium">
                  Rp
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formatNumberWithSeparator(buyPrice)}
                  onChange={(e) => handleBuyPriceChange(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
                  placeholder="0"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Satuan Dasar <span className="text-rose-500">*</span>
              </label>
              <SearchableSelect
                options={units}
                value={unitId}
                onChange={(value) => setUnitId(value)}
                onAddNew={handleAddUnit}
                addNewLabel="Tambah Satuan Baru"
                maxOptionsHeight="max-h-[110px]"
                fetchOptions={async (term) => {
                  const response = await masterService.units.list({
                    status: "ACTIVE",
                    query: term,
                  });
                  return response.data || [];
                }}
                minSearchLength={1}
                placeholder="Pilih satuan..."
                disabled={loading || loadingData}
                displayKey="name"
                valueKey="id"
              />
            </div>
          </div>

          {/* Harga Jual, Margin, & Stok Awal */}
          {isApps ? (
            /* Mode Kuliner: Harga Jual & Stok Awal bersisian jika tanpa varian */
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className={variants.length > 0 ? "sm:col-span-2" : ""}>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Harga Jual {variants.length === 0 && <span className="text-rose-500">*</span>}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm font-medium">
                    Rp
                  </span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatNumberWithSeparator(sellPrice)}
                    onChange={(e) => handleSellPriceChange(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
                    placeholder="0"
                    disabled={loading}
                  />
                </div>
              </div>

              {variants.length === 0 && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Stok Awal
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatNumberWithSeparator(stok)}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\./g, "");
                      if (raw && !/^\d+$/.test(raw)) return;
                      setStok(raw);
                    }}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
                    placeholder="0"
                    disabled={loading}
                  />
                </div>
              )}
            </div>
          ) : (
            /* Mode Retail: Baris 1: Harga Jual & Margin (%), Baris 2: Stok Awal (jika tanpa varian) */
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Harga Jual {variants.length === 0 && <span className="text-rose-500">*</span>}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm font-medium">
                      Rp
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatNumberWithSeparator(sellPrice)}
                      onChange={(e) => handleSellPriceChange(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
                      placeholder="0"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Margin (%)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={margin}
                      onChange={(e) => handleMarginChange(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-3 pr-8 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
                      disabled={loading}
                      step="0.01"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm font-medium">
                      %
                    </span>
                  </div>
                </div>
              </div>

              {variants.length === 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Stok Awal
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formatNumberWithSeparator(stok)}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/\./g, "");
                        if (raw && !/^\d+$/.test(raw)) return;
                        setStok(raw);
                      }}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
                      placeholder="0"
                      disabled={loading}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Status - Tampil saat Edit */}
          {initialData && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Status Produk <span className="text-rose-500">*</span>
              </label>
              <SearchableSelect
                options={[
                  { value: "Aktif", label: "Aktif" },
                  { value: "Non-Aktif", label: "Non-Aktif" },
                ]}
                value={status}
                onChange={(value) => setStatus(value)}
                placeholder="Pilih status..."
                disabled={loading}
                displayKey="label"
                valueKey="value"
              />
            </div>
          )}

          {/* Varian Produk (opsional) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold text-slate-700">
                Varian Produk{" "}
                <span className="text-slate-400 font-normal">(opsional)</span>
              </label>
              <button
                type="button"
                onClick={() =>
                  setVariants((prev) => [
                    ...prev,
                    { name: "", purchase_price: buyPrice ? buyPrice.toString() : "", price: "", stok: "" },
                  ])
                }
                disabled={loading}
                className="inline-flex items-center gap-1 text-xs font-semibold text-teal-600 hover:text-teal-700 disabled:opacity-50 transition-colors">
                <Plus className="w-3.5 h-3.5" />
                Tambah Varian
              </button>
            </div>

            {variants.length === 0 ? (
              <div className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 mb-3">
                  Belum ada varian produk yang ditambahkan.
                </p>
                <button
                  type="button"
                  onClick={() =>
                    setVariants([
                      { name: "", purchase_price: buyPrice ? buyPrice.toString() : "", price: "", stok: "" },
                    ])
                  }
                  disabled={loading}
                  className="w-full py-2.5 border-2 border-dashed border-slate-300 hover:border-teal-400 rounded-xl bg-white hover:bg-teal-50/30 text-teal-600 font-semibold text-xs inline-flex items-center justify-center gap-1.5 transition-all">
                  <Plus className="w-4 h-4" />
                  Tambah Baris Varian
                </button>
              </div>
            ) : (
              <div className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-4 space-y-3">
                {/* Header Label Kolom */}
                <div className="flex items-center gap-2.5 px-1">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-2.5">
                    <span className="text-xs font-semibold text-slate-600">Nama Varian</span>
                    <span className="text-xs font-semibold text-slate-600">Harga Modal</span>
                    <span className="text-xs font-semibold text-slate-600">Harga Jual</span>
                    <span className="text-xs font-semibold text-slate-600">Stok Awal</span>
                  </div>
                  <div className="w-10 flex-shrink-0 text-center">
                    <span className="text-xs font-semibold text-slate-600">Aksi</span>
                  </div>
                </div>

                {/* Daftar Varian */}
                <div className="space-y-2.5">
                  {variants.map((v, idx) => (
                    <div key={idx} className="flex items-center gap-2.5">
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-2.5">
                        <input
                          type="text"
                          value={v.name}
                          onChange={(e) =>
                            setVariants((prev) =>
                              prev.map((row, i) =>
                                i === idx
                                  ? { ...row, name: e.target.value }
                                  : row,
                              ),
                            )
                          }
                          placeholder="Small, Medium, dll."
                          disabled={loading}
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                        />
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium">
                            Rp
                          </span>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={formatNumberWithSeparator(v.purchase_price)}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/\./g, "");
                              if (raw && !/^\d+$/.test(raw)) return;
                              setVariants((prev) =>
                                prev.map((row, i) =>
                                  i === idx ? { ...row, purchase_price: raw } : row,
                                ),
                              );
                            }}
                            placeholder="0"
                            title="Harga Modal Varian"
                            disabled={loading}
                            className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                          />
                        </div>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium">
                            Rp
                          </span>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={formatNumberWithSeparator(v.price)}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/\./g, "");
                              if (raw && !/^\d+$/.test(raw)) return;
                              setVariants((prev) =>
                                prev.map((row, i) =>
                                  i === idx ? { ...row, price: raw } : row,
                                ),
                              );
                            }}
                            placeholder="0"
                            title="Harga Jual Varian"
                            disabled={loading}
                            className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                          />
                        </div>
                        <div>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={formatNumberWithSeparator(v.stok)}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/\./g, "");
                              if (raw && !/^\d+$/.test(raw)) return;
                              setVariants((prev) =>
                                prev.map((row, i) =>
                                  i === idx ? { ...row, stok: raw } : row,
                                ),
                              );
                            }}
                            placeholder="0"
                            title="Stok Awal Varian"
                            disabled={loading}
                            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setVariants((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                        disabled={loading}
                        aria-label="Hapus varian"
                        title="Hapus varian"
                        className="w-10 h-10 flex-shrink-0 inline-flex items-center justify-center rounded-lg bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 disabled:opacity-50 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Tombol Tambah Baris Varian */}
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() =>
                      setVariants((prev) => [
                        ...prev,
                        { name: "", purchase_price: buyPrice ? buyPrice.toString() : "", price: "", stok: "" },
                      ])
                    }
                    disabled={loading}
                    className="w-full py-2.5 border-2 border-dashed border-slate-300 hover:border-teal-400 rounded-xl bg-white/60 hover:bg-teal-50/40 text-teal-600 font-semibold text-xs inline-flex items-center justify-center gap-1.5 transition-all">
                    <Plus className="w-4 h-4" />
                    Tambah Baris Varian
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40 rounded-b-2xl flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-5 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm transition-colors disabled:opacity-50">
            Batal
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || loadingData}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium text-sm transition-colors shadow-sm shadow-teal-600/20 disabled:opacity-50">
            {loading ? "Menyimpan..." : "Simpan Data"}
          </button>
        </div>
      </div>
    </div>
  );
}
